import { formatRatingsChange, formatUpsideDownside } from '@benzinga/calendars';
import { ColumnDef } from '@benzinga/table';

import { WatchlistFinancials } from '../../../data';
import {
  SymbolCell,
  RESEARCH_SYMBOL_COLUMN_WIDTH,
  renderAction,
  formatCurrency,
  renderColoredValue,
  ResearchCell,
} from '../utils';

export const AnalystRatingsColumnsDef: ColumnDef<WatchlistFinancials>[] = [
  {
    field: 'symbol',
    headerName: 'Symbol',
    width: RESEARCH_SYMBOL_COLUMN_WIDTH,
    sortEnabled: true,
    cellRenderer: ({ data }) => <SymbolCell record={data} />,
    valueGetter: ({ data }) => data.symbol || '',
  },
  {
    field: 'date',
    headerName: 'Date',
    width: 110,
    sortEnabled: true,
    valueGetter: ({ data }) => data.ratings?.date || '',
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-20">
        <div className="text-sm">{value}</div>
      </ResearchCell>
    ),
  },
  {
    field: 'analyst',
    headerName: 'Firm',
    width: 140,
    sortEnabled: true,
    valueGetter: ({ data }) => data.ratings?.analyst || '',
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-24">
        <div className="text-sm">{value}</div>
      </ResearchCell>
    ),
  },
  {
    field: 'pt_current',
    headerName: 'Current Price Target',
    width: 200,
    sortEnabled: true,
    valueGetter: ({ data }) => data.ratings?.pt_current || 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-14">
        <div className="text-sm">{formatCurrency(Number(value))}</div>
      </ResearchCell>
    ),
  },
  {
    field: 'upside_downside',
    headerName: 'Upside/Downside Change',
    width: 220,
    sortEnabled: true,
    valueGetter: ({ data }) => {
      const price = data?.quote?.last ?? data?.quote?.lastTradePrice;
      const ptCurrent = data.ratings?.pt_current;
      if (!price || !ptCurrent) return 0;
      const change = formatUpsideDownside(price, ptCurrent);
      return typeof change === 'number' ? change : 0;
    },
    cellRenderer: ({ data }) => {
      const price = data?.quote?.last ?? data?.quote?.lastTradePrice;
      const ptCurrent = data.ratings?.pt_current;
      if (!price || !ptCurrent) {
        return (
          <ResearchCell hasValue={false} skeletonClassName="h-4 w-12">
            -
          </ResearchCell>
        );
      }

      const change = formatUpsideDownside(price, ptCurrent);
      if (!change) {
        return (
          <ResearchCell hasValue={false} skeletonClassName="h-4 w-12">
            -
          </ResearchCell>
        );
      }

      return renderColoredValue(change as number, 'percentage');
    },
  },
  {
    field: 'action_company',
    headerName: 'Rating',
    width: 120,
    sortEnabled: true,
    valueGetter: ({ data }) => data.ratings?.action_company || '',
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-16">
        {renderAction(value)}
      </ResearchCell>
    ),
  },
  {
    field: 'pt_prior',
    headerName: 'Previous/Current Rating',
    width: 220,
    sortEnabled: true,
    valueGetter: ({ data }) => data.ratings?.rating_prior || '',
    cellRenderer: ({ data }) => {
      const ratings = data.ratings;
      const hasValue = Boolean(ratings?.rating_prior && ratings?.rating_current);

      return (
        <ResearchCell hasValue={hasValue} skeletonClassName="h-4 w-28">
          {formatRatingsChange(ratings?.rating_prior ?? '', ratings?.rating_current ?? '', ratings?.action_company ?? '')}
        </ResearchCell>
      );
    },
  },
  {
    field: 'pt_pct_change',
    headerName: 'Price Target Change',
    width: 190,
    sortEnabled: true,
    valueGetter: ({ data }) => {
      const ptPctChange = data.ratings?.pt_pct_change;
      return typeof ptPctChange === 'number' ? ptPctChange : 0;
    },
    cellRenderer: ({ data }) => {
      const ratings = data.ratings;
      const hasValue = Boolean(ratings?.pt_prior && ratings?.pt_current);

      if (!hasValue) {
        return (
          <ResearchCell hasValue={false} skeletonClassName="h-4 w-20">
            -
          </ResearchCell>
        );
      }

      const ptPrior = typeof ratings?.pt_prior === 'number' ? ratings.pt_prior : 0;
      const ptCurrent = typeof ratings?.pt_current === 'number' ? ratings.pt_current : 0;
      const change = formatRatingsChange(formatCurrency(ptPrior), formatCurrency(ptCurrent), '');
      const ptPctChange = typeof ratings?.pt_pct_change === 'number' ? ratings.pt_pct_change : 0;

      return (
        <div className={`whitespace-nowrap ${ptPctChange > 0 ? 'text-bzgreen-500' : 'text-bzred-500'}`}>
          {change}
        </div>
      );
    },
  },
];
